// Data access for the content backend (.NET API over PostgreSQL JSONB).
//   - article list:     POST  {API_BASE}/api/Form/query  { form_Id: <index form>, fields: [...] }
//   - one row by slug:  the same call with  filters: { slug }   (used when a page is opened directly)
//   - one article:      GET   {API_BASE}/api/Form/data/record/{blogId},{content form id}
//   - update a record:  PATCH {API_BASE}/api/Form/{form id}     { id, data: {...}, updatedBy }     (preview page only)
// An index row carries `blogId`, the id of its document in the content form, and a `status`;
// only rows whose status is "published" are visible on the public site.
// Only this file knows about the API; pages and the renderer work with plain index rows and documents.

import { cfg } from '../../runtimeConfig';

const API_BASE = cfg('API_BASE', process.env.REACT_APP_API_BASE).replace(/\/$/, '');
const INDEX_FORM_ID = cfg('INDEX_FORM_ID', process.env.REACT_APP_INDEX_FORM_ID);
const CONTENT_FORM_ID = cfg('CONTENT_FORM_ID', process.env.REACT_APP_CONTENT_FORM_ID);
const MEDIA_BASE = cfg('MEDIA_BASE', process.env.REACT_APP_MEDIA_BASE).replace(/\/$/, '');
// Must be a user the backend knows: any other name makes PATCH fail with HTTP 500.
const UPDATED_BY = cfg('UPDATED_BY', process.env.REACT_APP_UPDATED_BY) || 'system';

export const STATUSES = [
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' },
  { value: 'archived', label: 'Archived' },
];

export function normalizeStatus(status) {
  const value = String(status || '').trim().toLowerCase();
  return STATUSES.some((s) => s.value === value) ? value : 'draft';
}

export const isPublished = (row) => String(row?.status || '').trim().toLowerCase() === 'published';

// Ask the backend only for what the site uses ("fields": [] would return everything).
// `id` always comes back. Fields a row does not have yet (author, category, featured image) are simply omitted.
const INDEX_FIELDS = [
  'blogId', 'slug', 'title', 'type', 'excerpt', 'tags', 'valid', 'status', 'version', 'wordCount', 'readingTimeMinutes',
  'importedAt', 'previousSlugs', 'sourceModified', 'authorName', 'categoryName', 'featuredMediaId',
  'featuredImageUrl', 'featuredImageAlt',
];

// When `fields` is given on /query, the backend returns every non-text value as a string ("true", "983", "[]", "{...}").
// Restore the real types for the known fields; values that already have the right type are left alone.
const JSON_FIELDS = ['tags', 'previousSlugs', 'meta', 'seo', 'content', 'source', 'media'];
const NUMBER_FIELDS = ['version', 'wordCount', 'readingTimeMinutes', 'schemaVersion'];
const BOOLEAN_FIELDS = ['valid'];

function restoreTypes(row) {
  const out = { ...row };
  for (const key of Object.keys(out)) {
    const value = out[key];
    if (typeof value !== 'string') continue;
    try {
      if (JSON_FIELDS.includes(key)) out[key] = JSON.parse(value);
      else if (NUMBER_FIELDS.includes(key)) out[key] = Number(value);
      else if (BOOLEAN_FIELDS.includes(key)) out[key] = value.toLowerCase() === 'true';
    } catch {
      // leave the raw value; the page shows an error for that article instead of crashing
    }
  }
  return out;
}

// `kind` lets the pages show a friendly message; `message` keeps the technical detail for the console and the preview pages.
const withKind = (kind, message) => Object.assign(new Error(message), { kind });
const unreachable = () => withKind('offline', `Cannot reach the content server at ${API_BASE || window.location.origin}`);

// A server that is down or hanging must not leave visitors on a spinner: give up after a few seconds.
const REQUEST_TIMEOUT_MS = 8000;
async function timedFetch(url, options) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

async function queryForm(formId, fields = [], filters) {
  if (!formId) throw withKind('config', 'Form id is not configured (see .env)');
  let res;
  try {
    res = await timedFetch(`${API_BASE}/api/Form/query`, {
      method: 'POST',
      // Accept */* on purpose: with "Accept: application/json" the backend double-encodes the list as a string.
      headers: { 'Content-Type': 'application/json', Accept: '*/*' },
      body: JSON.stringify(filters ? { form_Id: formId, fields, filters } : { form_Id: formId, fields }),
    });
  } catch {
    throw unreachable();
  }
  if (!res.ok) throw withKind('server', `Content server returned HTTP ${res.status}`);
  let data = await res.json();
  if (typeof data === 'string') data = JSON.parse(data); // tolerate a double-encoded response
  return Array.isArray(data) ? data.map(restoreTypes) : [];
}

async function getRecord(recordId, formId) {
  if (!formId) throw withKind('config', 'Form id is not configured (see .env)');
  let res;
  try {
    res = await timedFetch(`${API_BASE}/api/Form/data/record/${encodeURIComponent(recordId)},${encodeURIComponent(formId)}`, {
      headers: { Accept: 'text/plain' },
    });
  } catch {
    throw unreachable();
  }
  if (!res.ok) throw withKind('server', `Content server returned HTTP ${res.status}`);
  // The body is JSON served as text/plain; an unknown id gives an empty list.
  let data = JSON.parse(await res.text());
  if (typeof data === 'string') data = JSON.parse(data);
  const record = Array.isArray(data) ? data[0] : data;
  return record ? restoreTypes(record) : null;
}

// Cache the in-flight promise; forget it on failure so the next visit retries.
function memo(load) {
  let promise;
  const get = () => {
    if (!promise) {
      promise = load().catch((err) => {
        promise = undefined;
        throw err;
      });
    }
    return promise;
  };
  get.reset = () => {
    promise = undefined;
  };
  return get;
}

/** Summary rows of the articles visitors may see: valid and Published. */
export const fetchIndex = memo(async () =>
  (await queryForm(INDEX_FORM_ID, INDEX_FIELDS)).filter((entry) => entry.valid !== false && isPublished(entry)),
);

// Opening a page needs only the document id; `valid` and `status` keep other articles unreachable by link.
const LOOKUP_FIELDS = ['blogId', 'valid', 'status'];
const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/; // what the converter produces; anything else cannot exist
const entriesBySlug = new Map();

/**
 * The blogId for a Published article's slug, or null. Asks the backend for just that row and just those fields,
 * so opening an article directly needs neither the whole list nor the rest of the row.
 * An old slug is resolved through `previousSlugs` (redirect).
 */
export function fetchEntryBySlug(slug) {
  const key = String(slug || '').toLowerCase();
  if (!SLUG_PATTERN.test(key)) return Promise.resolve({ entry: null, movedTo: null }); // never sent to the server
  if (!entriesBySlug.has(key)) {
    const load = (async () => {
      const [row] = await queryForm(INDEX_FORM_ID, LOOKUP_FIELDS, { slug: key });
      if (row && row.valid !== false && isPublished(row)) return { entry: row, movedTo: null };
      const moved = (await fetchIndex()).find((e) => (e.previousSlugs || []).includes(key));
      return { entry: null, movedTo: moved || null };
    })();
    entriesBySlug.set(key, load);
    load.catch(() => entriesBySlug.delete(key)); // retry on the next visit
  }
  return entriesBySlug.get(key);
}

const documents = new Map();

/** The full document for an index row, loaded by the row's `blogId`. Cached per article. */
export function fetchDocument(entry) {
  if (!entry.blogId) return Promise.reject(new Error(`The article "${entry.slug}" has no document id (blogId)`));
  if (!documents.has(entry.blogId)) {
    const load = getRecord(entry.blogId, CONTENT_FORM_ID).then((doc) => {
      if (!doc) throw new Error(`The article "${entry.slug}" has no content on the server`);
      return doc;
    });
    documents.set(entry.blogId, load);
    load.catch(() => documents.delete(entry.blogId)); // retry on the next visit
  }
  return documents.get(entry.blogId);
}

function clearCaches() {
  fetchIndex.reset();
  entriesBySlug.clear();
  documents.clear();
}

/** Media registry keyed by mediaId. The API has no media endpoint, so pictures come from each document's `media` map. */
export async function fetchMedia() {
  return {};
}

export function mediaUrl(record) {
  if (!record?.url) return null;
  return /^(https?:)?\/\//.test(record.url) ? record.url : `${MEDIA_BASE}/${record.url}`;
}

// ---------------------------------------------------------------------------------------------
// Preview / editing. Never cached: the preview page must always show what is on the server now.
// ---------------------------------------------------------------------------------------------

/** Every row, whatever its status. Used by the preview list. */
export async function fetchAllEntries() {
  return queryForm(INDEX_FORM_ID, INDEX_FIELDS);
}

/** The full index row (with its record `id`) for a slug, in any status; null if there is none. */
export async function fetchPreviewEntry(slug) {
  const key = String(slug || '').toLowerCase();
  if (!SLUG_PATTERN.test(key)) return null;
  const [row] = await queryForm(INDEX_FORM_ID, INDEX_FIELDS, { slug: key });
  return row || null;
}

/** The document straight from the server (bypasses the cache). */
export async function fetchDocumentFresh(entry) {
  if (!entry.blogId) throw new Error(`The article "${entry.slug}" has no document id (blogId)`);
  const doc = await getRecord(entry.blogId, CONTENT_FORM_ID);
  if (!doc) throw new Error(`The article "${entry.slug}" has no content on the server`);
  return doc;
}

// PATCH merges top-level fields, but REPLACES nested objects as a whole: always send complete `meta`/`seo`/`media`.
// `activity` must not be sent (the server fails with HTTP 500 when it is present).
async function patchRecord(formId, id, data) {
  let res;
  try {
    res = await timedFetch(`${API_BASE}/api/Form/${encodeURIComponent(formId)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id, data, updatedBy: UPDATED_BY }),
    });
  } catch {
    throw unreachable();
  }
  if (!res.ok) throw new Error(`The server refused the change (HTTP ${res.status})`);
  return res.text();
}

/**
 * Save an article's details. The document is saved first (picture addresses, SEO, tags), then the list row
 * (status, tags, card picture), so an article is never published unless its details were stored.
 */
export async function saveArticle(entry, docData, rowData) {
  if (!entry.id || !entry.blogId) throw new Error('This article has no record id, so it cannot be saved');
  await patchRecord(CONTENT_FORM_ID, entry.blogId, docData);
  try {
    await patchRecord(INDEX_FORM_ID, entry.id, rowData);
  } catch (err) {
    clearCaches();
    throw new Error(`The article details were saved, but its list entry (status, tags) was not: ${err.message}`);
  }
  clearCaches();
}
